import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Briefcase, Receipt, FileText, TrendingUp, CheckCircle2 } from "lucide-react";
import { MaskedLines, FadeUp, Overline } from "@/components/Reveal";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";

const BLOCKS = [
  {
    icon: FileText,
    title: "Individual Tax Preparation",
    items: [
      "Federal, Pennsylvania, and local income tax returns",
      "Individual and married filing jointly returns",
      "Self-employment and Schedule C income",
      "Rental property income and expenses",
      "Investment income, interest, dividends, and capital gains",
      "Retirement and other common income sources",
    ],
    testid: "tax-individual",
  },
  {
    icon: Briefcase,
    title: "Small-Business Tax Preparation",
    items: [
      "Sole proprietor and single-member LLC filings",
      "Partnership tax returns",
      "S Corporation tax returns",
      "Coordination of business income with the owner's individual tax return",
    ],
    testid: "tax-business",
  },
  {
    icon: Calendar,
    title: "Estimated Tax Planning",
    items: [
      "Federal quarterly estimated taxes",
      "Pennsylvania estimated taxes",
      "Local estimated tax planning where applicable",
      "Tax projections to help identify potential obligations before filing season",
    ],
    testid: "tax-estimated",
  },
  {
    icon: TrendingUp,
    title: "Depreciation & Business Assets",
    items: [
      "Preparation and maintenance of depreciation schedules",
      "Tracking equipment, vehicles, furniture, machinery, rental property, and other business assets",
      "Review of asset purchases for appropriate tax treatment",
      "Tracking asset sales and disposals for future tax reporting",
    ],
    testid: "tax-depreciation",
  },
  {
    icon: Receipt,
    title: "Pennsylvania Sales & Use Tax",
    items: [
      "Pennsylvania sales tax return preparation and filing",
      "Review of sales tax collected and amounts due",
      "Assistance with reporting requirements",
      "Use tax reporting support",
    ],
    testid: "tax-pa-sales-tax",
  },
];

export default function TaxServices() {
  return (
    <div>
      <PageHeader
        testid="tax-header"
        overline="Tax Services"
        lines={["Practical tax preparation", "and filing support."]}
        intro="Arnold Bookkeeping provides practical tax preparation and filing support for individuals and small-business owners."
      />

      <section className="pb-24" data-testid="tax-blocks-section">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-6 md:grid-cols-2">
            {BLOCKS.map((b, i) => (
              <FadeUp key={b.title} delay={(i % 2) * 0.08} className="h-full">
                <div
                  data-testid={b.testid}
                  className="flex h-full flex-col rounded-2xl border border-hairline bg-white p-8 transition-shadow duration-300 hover:shadow-xl lg:p-10"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-parchment text-pine">
                    <b.icon size={22} />
                  </span>
                  <h2 className="mt-6 font-serif text-2xl font-semibold text-ink sm:text-3xl">{b.title}</h2>
                  <ul className="mt-6 flex-1 space-y-3">
                    {b.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5 text-sm leading-relaxed text-stone2">
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-gold" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeUp>
            ))}

            {/* Bookkeeping + Tax highlight card spans full width of the 2-col grid */}
            <FadeUp className="h-full md:col-span-2">
              <div className="relative overflow-hidden rounded-2xl bg-pine-950 text-cream" data-testid="tax-bookkeeping-plus-tax">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/f38635fe-5ba6-4f07-bccf-ef12c441b66a/images/938df604adddefbee54eeaef403c23e0ce904e69a6b790f6a9027f5ce317972e.jpeg"
                  alt="Blank 2026 tax return forms beside a cup of coffee"
                  className="absolute inset-0 h-full w-full object-cover opacity-25"
                  loading="lazy"
                  decoding="async"
                />
                <div className="relative p-10 lg:p-14">
                  <Overline light>Why it works better together</Overline>
                  <MaskedLines
                    as="h2"
                    inView
                    lines={["Bookkeeping + tax preparation."]}
                    className="mt-5 font-serif text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl"
                  />
                  <p className="mt-6 max-w-2xl text-lg leading-relaxed text-cream/80">
                    Bookkeeping and tax preparation work best when they work together. For
                    bookkeeping clients, Arnold Bookkeeping can maintain organized, reconciled
                    records throughout the year so tax preparation is based on accurate financial
                    information rather than a year-end scramble.
                  </p>
                  <Link
                    to="/contact"
                    data-testid="tax-bookkeeping-plus-tax-cta"
                    className="mt-9 inline-flex items-center gap-2 rounded-md bg-gold px-7 py-4 text-sm font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5"
                  >
                    Get Started <ArrowUpRight size={16} />
                  </Link>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
